import { apiFetch } from "@/auth/token";
import { friendlyError } from "@/api/friendlyError";
import type { PendingMeetingsSource } from "@/chat/hooks/useGranolaPending";

/**
 * /v1/meetings — Teams meetings for the Meetings page
 * (unified_agent/routes/meeting_routes.py).
 *
 * A meeting is "pending" once its transcript is ready and its organizer has not
 * decided yet. Accepting is consent for Artie to read the transcript and draft
 * notes for review; nothing is shared until those notes are approved.
 * Dismissing declines for good.
 */

export interface TeamsPendingMeeting {
  id: string;
  /** null when the meeting had no subject in Teams. */
  subject: string | null;
  /** ISO 8601 start time, or null. */
  occurred_at: string | null;
  duration_seconds: number | null;
  attendee_count: number;
}

export type MeetingStatus =
  | "accepted"
  | "processing"
  | "awaiting_review"
  | "indexing"
  | "approved"
  | "declined"
  | "expired"
  | "failed";

export interface MeetingHistoryItem extends TeamsPendingMeeting {
  status: MeetingStatus;
  /** ISO 8601 time of the decision (or of expiry). */
  decided_at: string | null;
}

export interface MeetingActionResult {
  ok: boolean;
  id: string;
  status: string;
}

/** Cookie-session request init, as in api/schedules.ts. */
function reqInit(extra?: RequestInit): RequestInit {
  return { credentials: "include", ...extra, headers: { ...(extra?.headers ?? {}) } };
}

async function jsonOk<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail: string | null = null;
    try {
      detail = ((await res.json()) as { detail?: string }).detail ?? null;
    } catch {
      // Non-JSON error body: fall back to the status message.
    }
    throw new Error(friendlyError(res.status, detail));
  }
  return res.json() as Promise<T>;
}

export async function listPendingMeetings(): Promise<TeamsPendingMeeting[]> {
  const r = await apiFetch("/v1/meetings/pending", reqInit());
  return (await jsonOk<{ pending: TeamsPendingMeeting[] }>(r)).pending;
}

export async function listMeetingHistory(): Promise<MeetingHistoryItem[]> {
  const r = await apiFetch("/v1/meetings/history", reqInit());
  return (await jsonOk<{ meetings: MeetingHistoryItem[] }>(r)).meetings;
}

export async function acceptMeeting(id: string): Promise<MeetingActionResult> {
  const r = await apiFetch(
    `/v1/meetings/${encodeURIComponent(id)}/accept`,
    reqInit({ method: "POST" }),
  );
  return jsonOk<MeetingActionResult>(r);
}

export async function dismissMeeting(id: string): Promise<MeetingActionResult> {
  const r = await apiFetch(
    `/v1/meetings/${encodeURIComponent(id)}/dismiss`,
    reqInit({ method: "POST" }),
  );
  return jsonOk<MeetingActionResult>(r);
}

/** The three calls usePendingMeetings needs, for the Waiting-for-you tab. */
export const pendingMeetingsSource: PendingMeetingsSource<TeamsPendingMeeting> = {
  listPending: listPendingMeetings,
  accept: acceptMeeting,
  dismiss: dismissMeeting,
};

// ---------------------------------------------------------------------------
// Review tab (unified_agent/routes/meeting_review_routes.py): the organizer
// edits the drafted notes, then adds them to the company knowledge base or
// discards them. Edits are accepted only while the draft is awaiting review.
// ---------------------------------------------------------------------------

/** The sections of the notes, in the order the PDF prints them. */
export type NoteCategory =
  | "decision"
  | "action_item"
  | "status"
  | "fact"
  | "requirement"
  | "risk"
  | "open_question"
  | "feedback"
  | "rejected_option"
  | "procedure";

export interface ReviewMeeting {
  id: string;
  subject: string | null;
  status: "awaiting_review" | "indexing";
  occurred_at: string | null;
  duration_seconds: number | null;
  item_count: number;
  /** "indexing_failed" when the last attempt to add the notes failed. */
  last_error: string | null;
  /** Indexing has gone quiet; approving again re-runs it. */
  stalled: boolean;
}

export interface DraftItem {
  id: string;
  category: NoteCategory | null;
  text: string;
  edited: boolean;
  owner: string | null;
  /** YYYY-MM-DD */
  due_date: string | null;
  start_ms: number | null;
  /** "12:34" into the meeting, or null for an item added by hand. */
  time: string | null;
  speakers: string[];
  low_confidence: boolean;
  source: "llm" | "manual";
}

export interface MeetingDraft {
  id: string;
  status: string;
  subject: string | null;
  occurred_at: string | null;
  duration_seconds: number | null;
  organizer: string | null;
  participants: string[];
  summary: string | null;
  last_error: string | null;
  items: DraftItem[];
}

export type DraftItemChange = Partial<Pick<DraftItem, "text" | "category" | "owner" | "due_date">>;

const meetingPath = (id: string) => `/v1/meetings/${encodeURIComponent(id)}`;
const json = (method: string, body: unknown): RequestInit =>
  reqInit({ method, body: JSON.stringify(body), headers: { "Content-Type": "application/json" } });

export async function listReviewMeetings(): Promise<ReviewMeeting[]> {
  const r = await apiFetch("/v1/meetings/review", reqInit());
  return (await jsonOk<{ meetings: ReviewMeeting[] }>(r)).meetings;
}

export async function getMeetingDraft(id: string): Promise<MeetingDraft> {
  return jsonOk<MeetingDraft>(await apiFetch(`${meetingPath(id)}/draft`, reqInit()));
}

/** The notes PDF as it will be stored. A blob, not a URL: the request needs
 * the session's credentials, which an iframe src cannot send. */
export async function fetchDraftPdf(id: string): Promise<Blob> {
  const r = await apiFetch(`${meetingPath(id)}/draft.pdf`, reqInit());
  if (!r.ok) await jsonOk(r);
  return r.blob();
}

export async function updateDraftSummary(id: string, summary: string): Promise<void> {
  await jsonOk(await apiFetch(`${meetingPath(id)}/summary`, json("PATCH", { summary })));
}

export async function updateDraftItem(
  id: string,
  itemId: string,
  change: DraftItemChange,
): Promise<void> {
  await jsonOk(
    await apiFetch(`${meetingPath(id)}/items/${encodeURIComponent(itemId)}`, json("PATCH", change)),
  );
}

export async function removeDraftItem(id: string, itemId: string): Promise<void> {
  await jsonOk(
    await apiFetch(
      `${meetingPath(id)}/items/${encodeURIComponent(itemId)}`,
      reqInit({ method: "DELETE" }),
    ),
  );
}

export async function addDraftItem(
  id: string,
  item: { text: string; category: NoteCategory | null },
): Promise<{ id: string }> {
  return jsonOk<{ id: string }>(await apiFetch(`${meetingPath(id)}/items`, json("POST", item)));
}

export async function approveMeeting(id: string): Promise<MeetingActionResult> {
  return jsonOk<MeetingActionResult>(
    await apiFetch(`${meetingPath(id)}/approve`, reqInit({ method: "POST" })),
  );
}

export async function discardMeeting(id: string): Promise<MeetingActionResult> {
  return jsonOk<MeetingActionResult>(
    await apiFetch(`${meetingPath(id)}/discard`, reqInit({ method: "POST" })),
  );
}
